import posthog from '@leyan/lytics';
import { UploadProps } from 'antd';
import { NimContext } from 'components/Nim';
import { set, isNil } from 'lodash-es';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  uploadLogInfoMap,
  uploadResource as uploadResourceApi,
  UploadResourceData,
} from 'services/api/uploads';
import { uploadFile } from 'services/api/resource';
import nimInst from 'services/nim';
import { UploadStateChangeEvent } from 'hooks/business/sendMessage';
import { deleteResourceMessage, getResourceMessage } from 'services/cache/resourceMessage';
import { logger } from 'services/logger';

export function useUploadResource() {
  const im = useContext(NimContext);

  const { on } = im;

  const { t } = useTranslate();

  const uploadResource = useCallback(
    (data: UploadResourceData) => {
      posthog.capture('$startUpload', {
        type: 'uploadResource',
        data,
      });
      return new Promise<{
        resourceId: string;
        url: string;
        localUrl?: string;
        fileName?: string;
        size?: string;
        externalId?: string;
      }>((resolve, reject) => {
        if (!data.file) {
          const error = new Error('uploadFileNull');

          set(error, 'failCause', {
            failType: 'A',
            code: 'upload_failed',
            desc: '',
            failMsgMap: {
              'zh-CN': t('ViewsChat.上传失败'),
              'en-US': t('ViewsChat.上传失败'),
            },
          } as Conversation.FailContent);

          reject(error);
        } else {
          uploadResourceApi(data)
            .then((res) => {
              logger.info(
                `[ResourceUpload]: ${JSON.stringify({
                  type: 'httpResponse',
                  data: res,
                })}`,
              );

              const { id, url, resourceState, localUrl, externalVideoId } = res;

              if (resourceState === 12) {
                const error = new Error('http resourceState === 12');

                set(error, 'failCause', {
                  failType: 'A',
                  code: 'upload_failed',
                  desc: '',
                  failMsgMap: {
                    'zh-CN': t('ViewsChat.上传失败'),
                    'en-US': t('ViewsChat.上传失败'),
                  },
                } as Conversation.FailContent);

                reject(error);

                return;
              }

              if (url && (resourceState === 8 || resourceState === undefined)) {
                resolve({
                  resourceId: id,
                  url,
                  localUrl,
                  externalId: externalVideoId,
                });

                return;
              }

              const handleMessage = (
                message: Resource.Message,
                handler?: (message: Resource.Message) => void,
              ) => {
                if (!message) {
                  return false;
                }

                const { resourceId, resourceState, url, fileName, size, externalVideoId } = message;

                if (!resourceId) {
                  return false;
                }

                logger.info(
                  `[ResourceUpload]: ${JSON.stringify({
                    type: 'yunxinMessage-useResourceUpload',
                    data: message,
                  })}`,
                );

                if (isNil(id) || `${resourceId}` !== `${id}`) {
                  return false;
                }

                const uploadInfo = uploadLogInfoMap[resourceId];
                delete uploadLogInfoMap[resourceId];

                if (resourceState === 8) {
                  resolve({
                    resourceId: id,
                    url,
                    localUrl,
                    fileName,
                    size,
                    externalId: externalVideoId,
                  });

                  handler?.(message);

                  if (uploadInfo) {
                    const uploadPlatformTime = Date.now();
                    const uploadPlatformDuration = uploadPlatformTime - uploadInfo.uploadOssTime;
                    posthog.capture('$uploadInfo', {
                      ...uploadInfo,
                      uploadPlatformTime,
                      uploadPlatformDuration,
                    });
                  }

                  return true;
                }

                if (resourceState === 12) {
                  const error = new Error('message resourceState === 12');

                  set(error, 'failCause', {
                    failType: 'A',
                    code: 'upload_failed',
                    desc: '',
                    failMsgMap: {
                      'zh-CN': t('ViewsChat.上传失败'),
                      'en-US': t('ViewsChat.上传失败'),
                    },
                  } as Conversation.FailContent);

                  reject(error);

                  handler?.(message);

                  return true;
                }

                return false;
              };

              const handled = handleMessage(getResourceMessage(id), (message) => {
                logger.info(
                  `[ResourceUpload]: ${JSON.stringify({
                    type: 'yunxin-before-http',
                    data: message,
                  })}`,
                );
              });

              deleteResourceMessage(id);

              if (handled) {
                return;
              }

              let timer: any;

              const off = on('message', (message: Resource.Message) =>
                handleMessage(message, () => {
                  off();

                  clearTimeout(timer);
                }),
              );

              timer = setTimeout(() => {
                off();

                const error = new Error('Timeout');

                set(error, 'failCause', {
                  failType: 'A',
                  code: 'upload_timeout',
                  desc: '',
                  failMsgMap: {
                    'zh-CN': '上传超时',
                    'en-US': 'Upload timed out',
                  },
                } as Conversation.FailContent);

                reject(error);
              }, 5 * 60 * 1000);
            })
            .catch((err) => {
              const error = new Error(err);

              set(error, 'failCause', {
                failType: 'A',
                code: 'upload_failed',
                desc: '',
                failMsgMap: {
                  'zh-CN': t('ViewsChat.上传失败'),
                  'en-US': t('ViewsChat.上传失败'),
                },
              } as Conversation.FailContent);

              reject(error);
            });
        }
      }).catch((err) => {
        posthog.capture('$uploadFailed', {
          type: 'uploadResource',
          error: String(err),
        });
        return Promise.reject(err);
      });
    },
    [on, t],
  );

  return uploadResource;
}

export function useCustomUpload() {
  const im = useContext(NimContext);

  const { on } = im;

  const offMapRef = useRef<Record<string, () => void>>({});

  const customUpload = useCallback(
    (
      props: Parameters<Required<UploadProps>['customRequest']>[0],
      data: Omit<UploadResourceData, 'file'>,
    ) => {
      const { onProgress, onSuccess, onError, file } = props;

      if (onProgress) {
        onProgress({
          percent: 20,
        } as Parameters<typeof onProgress>[0]);
      }

      uploadResourceApi({
        ...data,
        file,
      })
        .then((res) => {
          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'httpResponse',
              data: res,
            })}`,
          );

          const { id, url, localUrl, resourceState, resourceCentreId, externalVideoId } = res;

          if (localUrl && resourceCentreId && resourceCentreId !== '0') {
            if (onSuccess) {
              onSuccess(
                {
                  data: {
                    url: localUrl,
                    localUrl,
                    id: resourceCentreId,
                    resourceCentreId,
                    externalVideoId,
                  },
                },
                new XMLHttpRequest(),
              );
            }

            return;
          }

          if (resourceState === 12) {
            if (onError) {
              onError(new Error('Upload failed'));
            }

            return;
          }

          if (url && resourceState === 8) {
            if (onSuccess) {
              onSuccess({ data: { url, localUrl, id, externalVideoId } }, new XMLHttpRequest());
            }

            return;
          }

          if (onProgress) {
            onProgress({
              percent: 50,
            } as Parameters<typeof onProgress>[0]);
          }

          const off = on('message', (message: Resource.Message) => {
            const { resourceId, resourceState, url, externalVideoId } = message;

            if (`${resourceId}` !== id) {
              return;
            }

            logger.info(
              `[ResourceUpload]: ${JSON.stringify({
                type: 'yunxinMessage-useResourceUpload',
                data: message,
              })}`,
            );

            const uploadInfo = uploadLogInfoMap[resourceId];
            delete uploadLogInfoMap[resourceId];

            if (resourceState === 8) {
              if (onSuccess) {
                onSuccess(
                  { data: { url, localUrl, id, platformUrl: url, externalVideoId } },
                  new XMLHttpRequest(),
                );
              }

              off();

              delete offMapRef.current[id];

              if (uploadInfo) {
                const uploadPlatformTime = Date.now();
                const uploadPlatformDuration = uploadPlatformTime - uploadInfo.uploadOssTime;
                posthog.capture('$uploadInfo', {
                  ...uploadInfo,
                  uploadPlatformTime,
                  uploadPlatformDuration,
                });
              }
            }

            if (resourceState === 12) {
              if (onError) {
                onError(new Error(''));
              }

              off();

              delete offMapRef.current[id];
            }
          });

          offMapRef.current[id] = off;
        })
        .catch((err) => {
          if (onError) {
            onError(err);
          }
        });
    },
    [offMapRef, on],
  );

  useEffect(() => {
    const offList = Object.values(offMapRef.current);

    return () => offList.forEach((off) => off());
  }, []);

  return customUpload;
}

export function useCustomVideoUpload() {
  const customUpload = useCallback(
    (
      props: Parameters<Required<UploadProps>['customRequest']>[0],
      storeId?: string,
      region?: General.UpperCaseRegion,
      uploadResourceCentre?: boolean,
      storeGroupId?: string,
    ) => {
      const { onProgress, onSuccess, onError, file } = props;

      if (onProgress) {
        onProgress({
          percent: 20,
        } as Parameters<typeof onProgress>[0]);
      }

      uploadFile({
        storeId,
        storeGroupId,
        region,
        file: file as File,
        uploadResourceCentre,
      })
        .then((res) => {
          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'httpResponse',
              data: res,
            })}`,
          );

          const {
            localVideoUrl,
            resourceId,
            state,
            durationSeconds,
            thumbUrl,
            videoUrl,
            externalVideoId,
            resourceCentreId,
          } = res;

          if (resourceCentreId && resourceCentreId !== '0' && localVideoUrl) {
            onSuccess &&
              onSuccess(
                {
                  data: {
                    resourceCentreId,
                    externalVideoId: resourceCentreId,
                    url: localVideoUrl,
                    id: resourceCentreId,
                    durationSeconds,
                    thumbUrl,
                    platformUrl: localVideoUrl,
                  },
                },
                new XMLHttpRequest(),
              );
            return;
          }

          if (state === 4 && localVideoUrl && resourceId) {
            onSuccess &&
              onSuccess(
                {
                  data: {
                    externalVideoId,
                    url: localVideoUrl,
                    id: resourceId,
                    durationSeconds,
                    thumbUrl,
                    platformUrl: videoUrl,
                  },
                },
                new XMLHttpRequest(),
              );
            return;
          }

          if (state === 5 && onError) {
            onError && onError(new Error('Upload failed'));
            return;
          }

          if (onProgress) {
            onProgress({
              percent: 50,
            } as Parameters<typeof onProgress>[0]);
          }

          let timer: any;

          const off = nimInst.on('message', (message: any) => {
            const {
              resourceId: nimResourceId,
              resourceState,
              url,
              externalVideoId,
            } = (message as UploadStateChangeEvent) || {};

            if (!nimResourceId || String(nimResourceId) !== String(resourceId)) {
              return;
            }

            logger.info(
              `[ResourceUpload]: ${JSON.stringify({
                type: 'yunxinMessage-useResourceUpload',
                data: message,
              })}`,
            );

            if (resourceState === 4) {
              onSuccess &&
                onSuccess(
                  {
                    data: {
                      externalVideoId,
                      url: localVideoUrl,
                      id: resourceId,
                      platformUrl: url,
                      durationSeconds,
                      thumbUrl,
                    },
                  },
                  new XMLHttpRequest(),
                );
              off();
              clearTimeout(timer);
              const uploadInfo = uploadLogInfoMap[resourceId];
              if (uploadInfo) {
                const uploadPlatformTime = Date.now();
                const uploadPlatformDuration = uploadPlatformTime - uploadInfo.uploadOssTime;
                posthog.capture('$uploadInfo', {
                  ...uploadInfo,
                  uploadPlatformTime,
                  uploadPlatformDuration,
                });
              }
            }

            if (resourceState === 5) {
              onError && onError(new Error('Upload failed'));
              off();
              clearTimeout(timer);
            }

            delete uploadLogInfoMap[resourceId];
          });

          timer = setTimeout(() => {
            off();
            onError && onError(new Error('Upload failed'));
          }, 20 * 60 * 1000);
        })
        .catch((err) => {
          if (onError) {
            onError(err);
          }
        });
    },
    [],
  );

  return customUpload;
}
